import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { PageHeader } from '../../components';
import useComponentState from '../../utils/useComponentState';
import styles from './ViewVoucherThemes.module.scss';
import DataTable from '../../components/DataTable/DataTable';
import { Pagination } from '../../components';
import EditVoucherThemeModal from '../../components/Modals/EditVoucherThemeModal';
import { getVoucherThemes } from '../../api/voucherTheme';

const ViewVoucherThemes = (props) => {
  const [state, setState] = useComponentState({
    tableHeading: {
      column: [
        'Theme Name',
        'Category',
        'Front Image',
        'Back Image',
        'SMS Message',
        'Email Message',
      ],
    },
    voucherThemes: [],
    loading: false,
    notFoundMessage: '',
    fetchError: '',
    expanded: {},
    currentPage: 0,
    totalPages: 0,
    showEditModal: false,
    themeToEdit: null,
    categories: [],
  });

  const {
    voucherThemes,
    tableHeading,
    loading,
    notFoundMessage,
    fetchError,
    expanded,
    currentPage,
    totalPages,
    showEditModal,
    themeToEdit,
    categories,
  } = state;

  const fetchVoucherThemes = async (page = 0, pageSize = 5) => {
    setState({
      loading: true,
      voucherThemes: [],
      fetchError: '',
      notFoundMessage: '',
    });

    try {
      const response = await getVoucherThemes(
        props.auth.auth.token,
        page,
        pageSize
      );
      if (response && response.list && response.list.length > 0) {
        setState({
          voucherThemes: response.list,
          totalPages: response.totalPageCount || 0,
          currentPage: response.pageNo || page,
        });
      } else {
        setState({
          notFoundMessage: 'No voucher themes found',
        });
      }
    } catch (error) {
      setState({
        fetchError:
          error?.response?.status === 403
            ? 'Access denied. Please check your permissions.'
            : 'Failed to load voucher themes',
      });
    }
    setState({ loading: false });
  };
  const handleThemesPagination = (selectedPage) => {
    fetchVoucherThemes(selectedPage);
  };

  // Fetch voucher themes on component mount
  useEffect(() => {
    fetchVoucherThemes();
    // eslint-disable-next-line
  }, []);

  // Render image cell for front/back images
  const renderVoucherThemeImageCell = (imageUrl) => (
    <img
      src={imageUrl}
      alt='Theme preview'
      style={{
        width: '300px',
        height: '300px',
        objectFit: 'scale-down',
        borderRadius: '4px',
      }}
    />
  );

  // Handlers for edit modal
  const handleEditVoucherTheme = (theme) => {
    setState({
      showEditModal: true,
      themeToEdit: theme,
    });
  };

  const handleEditModalClose = () => {
    setState({
      showEditModal: false,
      themeToEdit: null,
    });
  };

  const handleVoucherUpdateSuccess = () => {
    fetchVoucherThemes(currentPage);
  };

  // Truncate text and provide expand/collapse option for SMS/Email content
  const renderExpandableText = (text, themeId, field, length = 100) => {
    if (!text) return '';
    const key = `${themeId}_${field}`;
    const isExpanded = expanded[key];

    if (isExpanded) {
      // Show full text with "Read Less" option
      return (
        <span>
          {text}{' '}
          <button
            type="button"
            onClick={() => {
              setState({
                expanded: {
                  ...expanded,
                  [key]: false,
                },
              });
            }}
            className={styles.readMoreLess}
            style={{ background: 'none', border: 'none', padding: 0, color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
          >
            ...Read Less
          </button>
        </span>
      );
    }

    // Show truncated text with "Read More" option
    return text.length > length ? (
      <span title={text}>
        {text.substring(0, length)}...{' '}
        <button
          type="button"
          onClick={() => {
            setState({
              expanded: {
                ...expanded,
                [key]: true,
              },
            });
          }}
          className={styles.readMoreLess}
          style={{ background: 'none', border: 'none', padding: 0, color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
        >
          Read More
        </button>
      </span>
    ) : (
      text
    );
  };

  const flatVoucherThemes = voucherThemes
    .filter(
      (group) =>
        Array.isArray(group.voucherThemes) && group.voucherThemes.length > 0
    )
    .flatMap((group) => group.voucherThemes);

  return (
    <div className={styles.root_div}>
      <PageHeader text='Voucher Themes' />

      {fetchError && <div className={styles.error}>{fetchError}</div>}

      {notFoundMessage && (
        <div className={styles.notFound}>{notFoundMessage}</div>
      )}
      {/* Data table for voucher themes */}
      <div className={styles.voucherTableWrapper}>
        <DataTable
          tableData={flatVoucherThemes}
          tableheading={tableHeading}
          tag='voucherThemes'
          renderVoucherThemeImageCell={renderVoucherThemeImageCell}
          renderExpandableText={renderExpandableText}
          expanded={expanded}
          fetchError={fetchError}
          search_filter_loading={loading}
          handleEditVoucherTheme={handleEditVoucherTheme}
        />
      </div>
      {/* Pagination controls */}
      {!loading && !fetchError && (
        <Pagination
          pageCount={totalPages}
          onChange={handleThemesPagination}
          currentPage={currentPage}
        />
      )}

      {/* Edit voucher theme modal */}
      <EditVoucherThemeModal
        show={showEditModal}
        onHide={handleEditModalClose}
        theme={themeToEdit}
        token={props.auth.auth.token}
        onSuccess={handleVoucherUpdateSuccess}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};

export default connect(mapStateToProps)(ViewVoucherThemes);
